import { c as _c } from "react/compiler-runtime";
import { useSceneSeatPermission } from "@components/table/members/assign-seats/use-scene-seat-permission";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast } from "@flat-ui";
import { MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPrettyName } from "@utils/user-utils";

/** Where the seats are changed from, as tracked, unless the caller says otherwise */
const DEFAULT_LOCATION = "workspace-members-table";

/** The prefix of the ID of the error toast when a single seat could not be given, per seat */
const ASSIGN_ERROR_ID_PREFIX = {
  xgSeat: "assignXgSeat",
  sceneSeat: "assignSceneSeat"
};
/** Returns the title of the error toast and its ID prefix when the seat changes failed */
function getErrorToast(changes) {
  const [change] = changes;
  if (!change || changes.length > 1) {
    return {
      idPrefix: "changeSeats",
      title: "Cannot change the seats"
    };
  }
  const label = MEMBER_SEAT_LABELS[change.seat];
  return change.isAssigning ? {
    idPrefix: ASSIGN_ERROR_ID_PREFIX[change.seat],
    title: `Cannot assign the ${label} seat`
  } : {
    idPrefix: "unassignSceneSeat",
    title: `Cannot remove the ${label} seat`
  };
}

/**
 * Returns how to give a member seats of the workspace subscription or take their SCENE seat away
 * again, with the request, its tracking and the toasts about it.
 *
 * All the changes go into one request, since the backend keeps only one of several requests that
 * change the permissions of a member at the same time.
 */
export function useChangeSeats() {
  const $ = _c(6);
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    setSeatPermissions,
    refreshSeatUsage
  } = useSceneSeatPermission();
  let t0;
  if ($[0] !== handleErrorWithToast || $[1] !== openToast || $[2] !== refreshSeatUsage || $[3] !== setSeatPermissions || $[4] !== trackEvent) {
    const changeSeats = async function changeSeats(member, changes, t1) {
      const location = t1 === undefined ? DEFAULT_LOCATION : t1;
      if (!changes.length) {
        return true;
      }
      for (const change of changes) {
        trackChange(change, location);
      }
      ;
      try {
        await setSeatPermissions(member, changes);
        refreshSeatUsage();
      } catch (t2) {
        const error = t2;
        const {
          idPrefix,
          title
        } = getErrorToast(changes);
        handleErrorWithToast({
          id: `${idPrefix}-${Date.now().toString()}`,
          title,
          error
        });
        return false;
      }
      for (const change_0 of changes) {
        openChangeToast(member, change_0);
      }
      return true;
    };
    function openChangeToast(member_0, change_1) {
      const label = MEMBER_SEAT_LABELS[change_1.seat];
      const name = <strong><var>{getPrettyName(member_0)}</var></strong>;
      if (change_1.isAssigning) {
        openToast({
          title: `${label} seat assigned`,
          message: <>{name} now holds a {label} seat.</>
        });
        return;
      }
      openToast({
        title: `${label} seat removed`,
        message: <>{name} lost their {label} seat, which is free again.</>
      });
    }
    function trackChange(t3, location_0) {
      const {
        seat,
        isAssigning
      } = t3;
      if (!isAssigning) {
        trackEvent({
          name: WorkspaceEvents.unassignSceneSeat,
          props: {
            numberOfMembers: 1,
            location: location_0
          }
        });
        return;
      }
      if (seat === "xgSeat") {
        trackEvent({
          name: WorkspaceEvents.assignXgSeat,
          props: {
            location: location_0
          }
        });
        return;
      }
      trackEvent({
        name: WorkspaceEvents.assignSceneSeats,
        props: {
          numberOfMembers: 1,
          location: location_0
        }
      });
    }
    t0 = {
      changeSeats
    };
    $[0] = handleErrorWithToast;
    $[1] = openToast;
    $[2] = refreshSeatUsage;
    $[3] = setSeatPermissions;
    $[4] = trackEvent;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}